// Pure active-view state machine for the PowerPoint add-in.
// Turns raw 'read'/'edit' view signals (from getActiveViewAsync / ActiveViewChanged /
// ResizeObserver re-sync) into a debounced decision. Mirrors Mentimeter's approach:
// a brief 'edit' blip during slide transitions must NOT drop us out of presenting,
// so 'edit' after 'read' is only committed if a re-query still reports 'edit' after a
// short debounce. A generation counter discards stale async re-query results.
//
// No Office.js or Vuex imports — all side effects are injected.

export const READ_TO_EDIT_DEBOUNCE_MS = 200

// onEnterRead fires on EVERY committed 'read' signal (not only the first), so returning
// to a slide after navigating away re-triggers it — intentional for auto-start callers.
export const createPptActiveViewMachine = ({ getActiveView, commitView, onEnterRead }) => {
  let hasBeenRead = false
  let pendingEditTimer = null
  let generation = 0

  const cancelPendingEdit = () => {
    if (pendingEditTimer !== null) {
      clearTimeout(pendingEditTimer)
      pendingEditTimer = null
    }
  }

  const handleView = (view) => {
    // Bumped on every signal so any in-flight re-query knows it has been superseded.
    generation += 1

    if (view === 'read') {
      hasBeenRead = true
      cancelPendingEdit()
      commitView('read')
      onEnterRead()
      return
    }

    // Any non-read view is treated as 'edit'.
    if (!hasBeenRead) {
      commitView('edit')
      return
    }

    // 'edit' after having been 'read': debounce + re-query before trusting it.
    cancelPendingEdit()
    const myGeneration = generation
    pendingEditTimer = setTimeout(async () => {
      pendingEditTimer = null
      let latest
      try {
        latest = await getActiveView()
      } catch (_) {
        if (myGeneration === generation) commitView('edit')
        return
      }
      if (myGeneration !== generation) return
      if (latest === 'edit') commitView('edit')
    }, READ_TO_EDIT_DEBOUNCE_MS)
  }

  const dispose = () => {
    cancelPendingEdit()
  }

  return { handleView, dispose }
}
